import React from "react";
import autobind from "class-autobind";
import { FormGroup } from "../../form/group/index";
import { Input } from "../../form/input/index";
import { InputBox } from "../../form/input.box/index";
import { Button } from "../../button/index";
import { FaBars, FaMinusCircle } from "react-icons/fa";
import {Tooltip} from "../../tooltip/index";
import {Checkbox} from "../../form/checkbox/index";
import {arrayMove, SortableContainer, SortableElement, SortableHandle} from "react-sortable-hoc";

type ItemType = RestaurantOptionSetOption;
type ListType = ItemType[];
type HandleChange = (i: number, key: keyof ItemType, value: string | number | null) => void;
type HandleSetDefault = (i: number, quantity: 1 | 0) => void;
type HandleRemove = (i: number) => void;
type HandleSort = (data: { oldIndex: number; newIndex: number }) => void;

interface ComponentProps {
  values: ListType;
  onChange: (values: ListType) => void;
  defaultEnabled?: boolean;
}
interface ListProps {
  items: ListType;
  change: HandleChange;
  remove: HandleRemove;
  setDefault: HandleSetDefault;
  defaultEnabled?: boolean;
}
interface ItemProps {
  item: ItemType;
  itemIndex: number;
  change: HandleChange;
  remove: HandleRemove;
  setDefault: HandleSetDefault;
  defaultEnabled?: boolean;
}

const DragHandle = SortableHandle(() => {
  return (
    <InputBox className="no-round width40 flex-center cursor-move">
      <p className="big font-bold">
        <FaBars/>
      </p>
    </InputBox>
  );
});
const SortableItem = SortableElement((props: ItemProps) => {
  const { item, itemIndex, change, remove, setDefault, defaultEnabled } = props;

  return (
    <div key={itemIndex} className="flex-line" style={{ alignItems: "flex-end" }}>

      <DragHandle/>

      <InputBox className="no-round width64 text-center">
        <p className="big font-bold" style={{ lineHeight: "36px" }}>{itemIndex + 1}</p>
      </InputBox>

      <div className="flex-line flex-grow" style={{ alignItems: "flex-end" }}>
        <FormGroup
          className="m-b-0 flex-grow"
          small_title={true}
          no_border={true}>
          <Input
            type="text"
            className="no-round"
            value={item.name}
            placeholder={"Name"}
            required={true}
            onChange={(e) => change(itemIndex, "name", e.target.value)}/>
        </FormGroup>
        <FormGroup
          className="m-b-0 flex-grow"
          small_title={true}
          no_border={true}>
          <Input
            type="number"
            min={0}
            step={0.01}
            className="no-round"
            placeholder={"Price ($)"}
            value={item.price || ""}
            onChange={(e) => change(itemIndex, "price", e.target.value)}/>
        </FormGroup>
        <FormGroup
          className="m-b-0 width100p"
          small_title={true}
          no_border={true}>
          <InputBox className="no-round flex-center">
            <Checkbox
              id={`${itemIndex}-unavailable`}
              checked={item.status === "not-available"}
              onChange={() => {
                console.log(item);
                change(itemIndex, "status", item.status === "not-available" ? null : "not-available");
                if (item.quantity === 1) {
                  setDefault(itemIndex, 0);
                }
              }}/>
            <p className="m-l-2">Unavailable</p>
          </InputBox>
        </FormGroup>
        {defaultEnabled && (
          <FormGroup
            className="m-b-0 width100p"
            small_title={true}
            no_border={true}>
            <InputBox className="no-round flex-center">
              <Checkbox
                id={`${itemIndex}-tax-compound`}
                checked={item.quantity === 1}
                onChange={() => setDefault(itemIndex, item.quantity === 1 ? 0 : 1)}/>
              <p className="m-l-2">Default</p>
            </InputBox>
          </FormGroup>
        )}
      </div>

      <Tooltip text="Delete" width={65} position="top" disable={itemIndex !== 0}>
        <Button type="button" className="no-round" paddinglr={5} color="white" onClick={() => remove(itemIndex)}>
          <FaMinusCircle/>
        </Button>
      </Tooltip>

    </div>
  );
});
const SortableItemContainer = SortableContainer((props: ListProps) => {
  return (
    <div>
      {props.items.map((item, i) => (
        <SortableItem
          key={`item-${i}`}
          index={i}
          item={item}
          itemIndex={i}
          change={props.change}
          remove={props.remove}
          setDefault={props.setDefault}
          defaultEnabled={props.defaultEnabled}
        />
      ))}
    </div>
  );
});

export class FieldOptionSetOptions extends React.Component<ComponentProps> {

  constructor(props: ComponentProps) {
    super(props);
    autobind(this);
  }

  remove: HandleRemove = (i) => {
    const values = ([ ...this.props.values ]);
    values.splice(i, 1);
    this.props.onChange(values);
  }
  change: HandleChange = (i, key, value) => {
    const values = [ ...this.props.values ];
    values[i][key] = value;
    this.props.onChange(values);
  }
  setDefault: HandleSetDefault = (i, quantity) => {
    this.props.onChange(this.props.values.map((item, index) => ({
      ...item,
      quantity: i === index ? quantity : 0,
      status: i === index ? null : item.status,
    })));
  }
  onSortEnd: HandleSort = ({oldIndex, newIndex}) => {
    const { onChange, values } = this.props;
    onChange(arrayMove(values, oldIndex, newIndex));
  }

  render() {
    const { values, defaultEnabled } = this.props;
    return (
      <SortableItemContainer
        helperClass="zindex-15 bsl-2"
        pressDelay={50}
        useDragHandle={true}
        items={values}
        change={this.change}
        remove={this.remove}
        setDefault={this.setDefault}
        defaultEnabled={defaultEnabled}
        onSortEnd={this.onSortEnd}
      />
    );
  }
}
